import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import { useLang } from "@/contexts/LanguageContext";
import { getCollections, staticImg } from "@/lib/api";

export default function CollectionsPage() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const location = useLocation();

  useEffect(() => {
    getCollections().then(setItems).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (location.hash && items.length) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [location.hash, items]);

  return (
    <div data-testid="collections-page">
      {/* HERO */}
      <Section testid="collections-hero" className="pt-32 md:pt-36">
        <Container>
          <div className="max-w-4xl">
            <Reveal><Eyebrow>{t.nav.collections}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] mt-6 whitespace-pre-line">
                {t.collections.hero_h1}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl text-base md:text-[17px] text-arven-offwhite/70 leading-relaxed">
                {t.collections.hero_sub}
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* COLLECTIONS LIST */}
      <Container className="pb-28">
        <div className="space-y-24 md:space-y-32">
          {items.map((c, i) => {
            const reversed = i % 2 === 1;
            return (
              <div key={c.id} id={c.slug} className="scroll-mt-32">
                <Reveal>
                  <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-center ${reversed ? "lg:[direction:rtl]" : ""}`}>
                    <div className="lg:col-span-7 [direction:ltr]">
                      <div className="relative overflow-hidden rounded-2xl aspect-[4/3] kbd-card">
                        <img
                          src={staticImg(c.image)}
                          alt={c.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div className="lg:col-span-5 [direction:ltr]">
                      <Eyebrow>{`0${i + 1} — ${t.collections.eyebrow}`}</Eyebrow>
                      <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-tight mt-5">
                        {lang === "fr" ? c.name : c.name_en}
                      </h2>
                      <p className="mt-4 text-arven-gold text-sm tracking-wider uppercase">
                        {lang === "fr" ? c.tagline : c.tagline_en}
                      </p>
                      <p className="mt-6 text-[15px] md:text-base leading-relaxed text-arven-offwhite/70">
                        {lang === "fr" ? c.description : c.description_en}
                      </p>
                      <div className="mt-7 flex flex-wrap gap-2">
                        {c.materials.map((m) => (
                          <span key={m} className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase border border-white/15 rounded-full text-arven-offwhite/75">
                            {m}
                          </span>
                        ))}
                      </div>
                      <Link to="/projets" className="btn-text-gold mt-8" data-testid={`collection-cta-${c.slug}`}>
                        {t.common.discoverMore} <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              </div>
            );
          })}
        </div>
      </Container>

      {/* CTA */}
      <Section testid="collections-cta" className="bg-arven-anthracite border-t border-white/5 noise-overlay relative overflow-hidden">
        <Container>
          <div className="max-w-3xl mx-auto text-center py-6 md:py-12">
            <Reveal>
              <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-tight whitespace-pre-line">
                {t.collections.cta_h2}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9">
                <Link to="/rendez-vous" className="btn-gold">
                  {t.common.appointment} <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </div>
  );
}
